import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { VideoTile } from "@/components/VideoTile";

const CATEGORIES = ["All", "Race Recap", "HYROX", "Community", "Founder Notes"];

const TAPES = [
  { n: "06", caption: "PACK MILES" },
  { n: "07", caption: "TWO-UP TEMPO" },
  { n: "08", caption: "TRACK TUESDAY" },
  { n: "09", caption: "LAST LIGHT LAPS" },
];

export default function Stories() {
  const [stories, setStories] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageMeta("Stories From the Miles | Ninja Run Club", "Race recaps, community stories, HYROX lessons, and founder notes from the Ninja Run Club movement — shot at events we actually showed up for.");
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = category !== "All" ? { category } : {};
    api.get("/stories", { params }).then((r) => setStories(r.data)).finally(() => setLoading(false));
  }, [category]);

  useEffect(() => {
    if (stories.length === 0) return;
    return setJsonLd("stories-jsonld", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Stories from the miles",
      numberOfItems: stories.length,
      itemListElement: stories.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.title,
        item: `${SITE_BASE}/stories/${s.slug}`,
      })),
    });
  }, [stories]);

  const [feature, ...rest] = stories;

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <Reveal>
          <p className="font-mono-label text-white/40">REAL FOOTAGE. REAL MILES.</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">STORIES FROM THE MILES.</h1>
          <p className="text-white/50 text-sm max-w-2xl mt-4">
            Race recaps, community moments, and lessons earned under fatigue — every story here was shot at an event we
            actually raced or hosted, with the takeaways that survived contact with the course. No stock footage, no
            recycled motivation. Just what happened, what it taught us, and how to train for the next one.
          </p>
        </Reveal>
      </header>

      <section data-testid="stories-tapes" className="px-5 lg:px-10 py-10 border-b border-white/10">
        <Reveal>
          <div className="flex items-baseline justify-between gap-4">
            <p className="font-mono-label text-white/40">THE TAPES · FRESH FROM THE TRACK</p>
            <p className="font-mono-label text-white/25 hidden sm:block">MUTED · LOOPING</p>
          </div>
        </Reveal>
        <div className="mt-6 flex gap-px overflow-x-auto snap-x snap-mandatory lg:grid lg:grid-cols-4 bg-white/10 border border-white/10">
          {TAPES.map((t, i) => (
            <div key={t.n} className="snap-start shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto">
              <VideoTile
                src={`/video/run-${t.n}.mp4`}
                srcMobile={`/video/run-${t.n}-mobile.mp4`}
                srcWebm={`/video/run-${t.n}.webm`}
                poster={`/video/run-${t.n}-poster.jpg`}
                caption={t.caption}
                className="aspect-[4/5] h-full"
                testid={`tape-tile-${i}`}
                allowSound
              />
            </div>
          ))}
        </div>
      </section>

      <div data-testid="stories-category-filters" className="sticky top-16 z-40 bg-night/90 backdrop-blur-xl border-b border-white/10 px-5 lg:px-10 py-4 flex gap-2 overflow-x-auto">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`stories-category-${c.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setCategory(c)}
            className={`font-mono-label px-4 py-2.5 border whitespace-nowrap transition-colors duration-200 ${
              category === c ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="px-5 lg:px-10 py-10">
        {loading ? (
          <p className="font-mono-label text-white/40 py-16 text-center">LOADING...</p>
        ) : stories.length === 0 ? (
          <div data-testid="stories-empty" className="border border-white/10 p-14 text-center my-10">
            <p className="font-display text-3xl">NO STORIES HERE YET.</p>
            <p className="text-white/50 text-sm mt-3">The next one is being written on the road.</p>
          </div>
        ) : (
          <>
            {feature && (
              <Link to={`/stories/${feature.slug}`} data-testid={`story-feature-${feature.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 border border-white/10 mb-px hover:bg-white hover:text-ink transition-colors duration-300">
                <div className="aspect-[4/5] lg:aspect-auto lg:min-h-[440px]">
                  {feature.hero?.type === "video" ? (
                    <VideoTile src={feature.hero.src} poster={feature.hero.poster} className="h-full" />
                  ) : (
                    <img src={feature.hero?.src} alt={feature.title} className="img-bw h-full w-full object-cover" />
                  )}
                </div>
                <div className="p-8 lg:p-14 flex flex-col justify-center">
                  <p className="font-mono-label opacity-50">{feature.category} · {feature.date}</p>
                  <h2 className="font-display text-3xl lg:text-5xl mt-4 leading-[0.98]">{feature.title}</h2>
                  <p className="text-sm opacity-60 mt-5 max-w-md">{feature.excerpt}</p>
                  <span className="font-mono-label mt-8 opacity-70">READ THE STORY →</span>
                </div>
              </Link>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 border-t-0" data-testid="stories-grid">
              {rest.map((s) => (
                <Link key={s.slug} to={`/stories/${s.slug}`} data-testid={`story-card-${s.slug}`} className="group bg-night hover:bg-white hover:text-ink transition-colors duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    {s.hero?.type === "video" ? (
                      <VideoTile src={s.hero.src} poster={s.hero.poster} className="h-full" />
                    ) : (
                      <img src={s.hero?.src} alt={s.title} className="img-bw h-full w-full object-cover" />
                    )}
                  </div>
                  <div className="p-7">
                    <p className="font-mono-label opacity-50">{s.category} · {s.date}</p>
                    <h2 className="font-display text-2xl mt-3 leading-tight">{s.title}</h2>
                    <p className="text-sm opacity-60 mt-3">{s.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
